import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FloatField } from "@/components/ui/FormField";
import { Loader2 } from "lucide-react";

// ── Types ──────────────────────────────────────────────────────────────────────

export interface SplitFormField {
  name: string;
  label: string;
  type?: "text" | "email" | "tel" | "textarea" | "select" | "date";
  placeholder?: string;
  required?: boolean;
  options?: string[];
  half?: boolean;
}

export interface SplitFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;

  heading: string;
  subheading?: string;

  // Kept for backward compat — not rendered
  badge?: string;
  benefits?: string[];
  brandGradient?: string;
  formHeading?: string;
  formSubheading?: string;
  successMessage?: string;

  fields: SplitFormField[];
  submitLabel?: string;

  onSubmit: (data: Record<string, string>) => Promise<void> | void;
}

// ── Component ──────────────────────────────────────────────────────────────────

export function SplitFormModal({
  open,
  onOpenChange,
  heading,
  subheading,
  fields,
  submitLabel = "Submit",
  onSubmit,
}: SplitFormModalProps) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = (name: string, value: string) => {
    setValues((v) => ({ ...v, [name]: value }));
    setErrors((e) => { const n = { ...e }; delete n[name]; return n; });
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    fields.forEach((f) => {
      if (f.required && !values[f.name]?.trim())
        errs[f.name] = "This field is required";
      if (f.type === "email" && values[f.name] && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values[f.name]))
        errs[f.name] = "Please enter a valid email";
    });
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    try {
      await onSubmit(values);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => { setValues({}); setErrors({}); }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{heading}</DialogTitle>
          {subheading && <DialogDescription>{subheading}</DialogDescription>}
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-4">
            {fields.map((field) => (
              <div key={field.name} className={!field.half ? "md:col-span-2" : ""}>
                <FloatField
                  label={field.label}
                  type={field.type}
                  placeholder={field.placeholder}
                  required={field.required}
                  options={field.options}
                  value={values[field.name] ?? ""}
                  error={errors[field.name]}
                  onChange={(v) => set(field.name, v)}
                />
              </div>
            ))}
          </div>

          <DialogFooter className="mt-6">
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="gap-2">
              {submitting
                ? <><Loader2 className="h-4 w-4 animate-spin" />Sending…</>
                : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
